import { createRouter } from '@tanstack/react-router'

import { routeTree } from '@/app/routes.gen'
import { getEnv } from '@/libs/runtime-env/runtimeEnv'
import { isUuid, isValidGitHash } from '@/utils/string'

const router = createRouter({
  routeTree,
  // @ts-ignore don't want to define this here since we only use the router in this file to get routesByPath below
  context: {},
})

const topLevelFeRoutes = [
  ...new Set(
    Object.keys(router.routesByPath)
      .map((path) => path.split('/')[1])
      .filter(Boolean),
  ),
]

type DevDeploymentPrefix = {
  feVersion?: string
  beVersion?: string
}

/**
 * Parses optional FE/BE deployment prefixes from dev URLs.
 *
 * Supported shapes:
 * - /{feHash}/spaces/...
 * - /{feHash}/{beVersion}/spaces/...
 * - /{feHash}/{companyId}/spaces/...
 * - /{feHash}/{beVersion}/{companyId}/spaces/...
 */
export const parseDevDeploymentPrefix = (
  pathname: string,
): DevDeploymentPrefix => {
  const segments = pathname.split('/').filter(Boolean)

  if (segments.length === 0 || !isValidGitHash(segments[0])) {
    return {}
  }

  const feVersion = segments[0]
  const rest = segments.slice(1)

  if (rest.length === 0) {
    return { feVersion }
  }

  const [second, ...remaining] = rest

  if (topLevelFeRoutes.includes(second) || isUuid(second)) {
    return { feVersion }
  }

  const beVersion = second

  if (remaining.length > 0 && isUuid(remaining[0])) {
    return { feVersion, beVersion }
  }

  if (remaining.length > 0 && topLevelFeRoutes.includes(remaining[0])) {
    return { feVersion, beVersion }
  }

  return { feVersion, beVersion }
}

export const getBasePathInfo = (
  hostname: string,
  pathname: string,
): string | undefined => {
  // localhost
  if (hostname === 'localhost') {
    return getEnv('VITE_APP_STATIC_URL')
  }

  const { feVersion, beVersion } = parseDevDeploymentPrefix(pathname)

  if (!feVersion) {
    return undefined
  }

  if (beVersion) {
    return `${feVersion}/${beVersion}`
  }

  return feVersion
}

export const basePathInfo = getBasePathInfo(
  window.location.hostname,
  window.location.pathname,
)

export const isProd = window.location.hostname === 'ai.cloudfactory.app'

/**
 * Determine API backend URLs based on the frontend domain
 */
export function getApiUrls(hostname: string, pathname: string) {
  const isProd = hostname === 'ai.cloudfactory.app'

  // If environment variables are set, use them (for local development)
  const configuredAiUrl = getEnv('VITE_APP_AI_API_BASE_URL')
  const configuredAipUrl = getEnv('VITE_APP_AIP_API_BASE_URL')

  if (configuredAiUrl && configuredAipUrl) {
    return {
      ai: configuredAiUrl,
      aip: configuredAipUrl,
    }
  }

  // localhost - use dev backend
  if (hostname === 'localhost') {
    return {
      ai: 'https://default.shared.ai.cloudfactory.app/ai',
      aip: 'https://default.shared.ai.cloudfactory.app/aip',
    }
  }

  // dev.ai.cloudfactory.app => master/sha fe + dev back
  if (hostname === 'dev.ai.cloudfactory.app') {
    const { feVersion, beVersion } = parseDevDeploymentPrefix(pathname)
    const resolvedBeVersion = feVersion && beVersion ? beVersion : 'default'

    return {
      ai: `https://${resolvedBeVersion}.shared.ai.cloudfactory.app/ai`,
      aip: `https://${resolvedBeVersion}.shared.ai.cloudfactory.app/aip`,
    }
  }

  if (isProd) {
    return {
      ai: 'https://api.ai.cloudfactory.app/ai',
      aip: 'https://api.ai.cloudfactory.app/aip',
    }
  }

  // Fallback to dev
  return {
    ai: 'https://default.shared.ai.cloudfactory.app/ai',
    aip: 'https://default.shared.ai.cloudfactory.app/aip',
  }
}

// Get dynamic API URLs based on current domain
const apiUrls = getApiUrls(window.location.hostname, window.location.pathname)

// AI Backend (Python)
export const aiApiBaseUrl = apiUrls.ai
// AIP Backend (Go)
export const aipApiBaseUrl = apiUrls.aip
